import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import Toasts from "../../../core/common/toast/index";
import {
  approveRejectTask,
  clearError,
  clearNocData,
  clearNocError,
  fetchSapWorklist,
  syncWorklistCurrentDay,
  setCurrentPage,
  setSelectedTask,
  WorklistItem,
} from "../../../core/data/redux/sapWorklistSlice";
import UniversalWorklistLayout from "./components/UniversalWorklistLayout";
import {
  getWorklistTypeLabel,
  isMedicalAttendanceWorklistItem,
} from "../otherServices/medicalAttendance/medicalAttendance.service";
import { isLeaveEncashmentWorklistItem } from "../otherServices/leaveEncashment/leaveEncashment.service";
import { isHallBookingWorklistItem } from "../otherServices/communityHall/communityHall.service";
import { isStaffWelfareWorklistItem } from "../otherServices/staffWelfareFund/staffWelfare.service";
import { isEAttestationWorklistItem } from "../eAttestation/eAttestation.service";
import { getSv4ApprovalPath, isSv4WorklistItem } from "../sv4Card/sv4Card.service";

const isSelfTransferWorklistItem = (task: any): boolean => {
  const type = String(task?.type || "").toUpperCase();
  const taskText = String(task?.taskText || "").toLowerCase();
  const workItemText = String(task?.workItemText || "").toLowerCase();
  return (
    type === "SELF_TRANSFER" ||
    type === "TRANSFER_POSTING" ||
    type === "TRANSFER" ||
    taskText.includes("self transfer") ||
    taskText.includes("transfer posting") ||
    workItemText.includes("self transfer") ||
    workItemText.includes("transfer posting")
  );
};


const defaultModuleTypeOptions: { value: string; label: string }[] = [
  { value: "", label: "All" }
];

const defaultClaimTypeOptions: { value: string; label: string }[] = [
  { value: "", label: "All Claim Types" }
];

const dateRangeOptions = [
  { value: "", label: "All" },
  { value: "TODAY", label: "Today" },
  { value: "YESTERDAY", label: "Yesterday" },
  { value: "LAST_3_DAYS", label: "Last 3 Days" },
  { value: "THIS_WEEK", label: "This Week" },
  { value: "CUSTOM", label: "Custom Range" }
];

interface TasksPageProps { }

const TasksPage: React.FC<TasksPageProps> = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Redux state
  const {
    worklistData,
    nocData,
    loading,
    nocLoading,
    actionLoading,
    error,
    nocError,
    selectedTask,
    pagination,
    syncLoading,
  } = useSelector((state: any) => state.sapWorklistState);

  // Local state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  const [moduleTypeOptions, setModuleTypeOptions] = useState(defaultModuleTypeOptions);
  const [claimTypeOptionsList, setClaimTypeOptionsList] = useState(defaultClaimTypeOptions);

  useEffect(() => {
    const fetchFilterMasters = async () => {
      try {
        const moduleRes = await Api.post(API_URLS.SAP_MASTER_WORKLIST.MODULE_TYPES, {});
        if (moduleRes.data && Array.isArray(moduleRes.data.data)) {
          const mappedModules = moduleRes.data.data.map((item: any) => ({
            label: item.module_name,
            value: item.module_value,
          }));
          setModuleTypeOptions([{ label: "All", value: "" }, ...mappedModules]);
        }
      } catch (e) {
        console.log("Error fetching module types", e);
      }

      try {
        const claimRes = await Api.post(API_URLS.reimbursement.GET_MASTERS, { type: "Reimbursement_Type" });
        if (claimRes.data && claimRes.data.data && claimRes.data.data.Reimbursement_Type) {
          const mappedClaims = claimRes.data.data.Reimbursement_Type.map((item: any) => ({
            label: item.type_text,
            value: item.type_id,
          }));
          setClaimTypeOptionsList([{ label: "All Claim Types", value: "" }, ...mappedClaims]);
        }
      } catch (e) {
        console.log("Error fetching claim types", e);
      }
    };
    fetchFilterMasters();
  }, []);

  const [filters, setFilters] = useState({
    dateRange: { type: "", from: "", to: "" },
    moduleType: "",
    claimType: "",
    referenceNumber: "",
  });

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    type: "success" as "success" | "danger" | "warning" | "primary",
  });

  // Helper function to show toast
  const showToast = (
    title: string,
    message: string,
    type: "success" | "danger" | "warning" | "primary"
  ) => {
    // Format message to be user-friendly (Bug 47)
    const formattedMessage = formatErrorMessage(message);

    setToast({
      show: true,
      title,
      message: formattedMessage,
      type,
    });
    // Auto-hide after 5 seconds
    setTimeout(() => {
      setToast((prev) => ({ ...prev, show: false }));
    }, 5000);
  };

  const formatErrorMessage = (message: string): string => {
    if (!message || typeof message !== "string") return message;

    // If it's a long message or doesn't look like a key, return as is
    if (message.includes(" ") && !message.includes("_")) return message;

    return message
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
      .replace(/D&ar/i, "D&AR")
      .replace(/Noc/i, "NOC")
      .replace(/Id/i, "ID");
  };

  const getTaskIcon = (task: WorklistItem): string => {
    const taskText = task.taskText?.toLowerCase() || "";
    const type = task.type?.toLowerCase() || "";

    if (isStaffWelfareWorklistItem(task)) return "ti ti-heart-handshake";
    if (isEAttestationWorklistItem(task)) return "ti ti-certificate";
    if (isMedicalAttendanceWorklistItem(task)) return "ti ti-heartbeat";
    if (isHallBookingWorklistItem(task)) return "ti ti-building";
    if (isLeaveEncashmentWorklistItem(task)) return "ti ti-cash";
    if (isSv4WorklistItem(task)) return "ti ti-id-badge-2";
    if (isSelfTransferWorklistItem(task)) return "ti ti-arrows-transfer-up";
    if (type.includes("noc") || taskText.includes("noc"))
      return "ti ti-file-certificate";
    if (taskText.includes("leave")) return "ti ti-calendar-time";
    if (taskText.includes("attendance")) return "ti ti-clock";
    if (taskText.includes("overtime")) return "ti ti-clock-plus";
    return "ti ti-file-text";
  };

  const getTaskType = (task: WorklistItem): string => {
    if (isStaffWelfareWorklistItem(task)) {
      return String(task.workItemText || "").trim() || "Staff Welfare Fund";
    }
    if (isEAttestationWorklistItem(task)) {
      return String(task.workItemText || "").trim() || "E-Attestation Process";
    }
    if (isMedicalAttendanceWorklistItem(task)) return getWorklistTypeLabel(task);
    if (isHallBookingWorklistItem(task)) return "Community Hall Booking";
    if (isLeaveEncashmentWorklistItem(task)) return "Leave Encashment";
    if (isSv4WorklistItem(task)) return "SV4 Card";
    if (isSelfTransferWorklistItem(task)) return "Self Transfer";
    const taskText = task.taskText?.toLowerCase() || "";
    const type = task.type?.toLowerCase() || "";

    if (type.includes("noc") || taskText.includes("noc")) return "NOC Request";
    if (taskText.includes("leave")) return "Leave Request";
    if (taskText.includes("attendance")) return "Attendance";
    if (taskText.includes("overtime")) return "Overtime Request";
    return getWorklistTypeLabel(task);
  };

  const getTaskTypeBadge = (task: WorklistItem) => {
    const baseClass = "badge ms-2";
    switch (task.type) {
      case "NOC_OUTJOB":
        return { className: `${baseClass} bg-warning text-dark`, style: {} };
      case "NOC_HIGHERSTUDY":
        return { className: `${baseClass} bg-info text-white`, style: {} };
      case "NOC_PASSPORT":
        return { className: `${baseClass}`, style: { backgroundColor: '#6f42c1', color: 'white' } };
      case "NOC_EXINDIA":
        return { className: `${baseClass} bg-primary text-white`, style: {} };
      case "NOC_ONLINECOURSE":
      case "NOC_ONLINECOURSE-REIMB":
        return { className: `${baseClass}`, style: { backgroundColor: '#fd7e14', color: 'white' } };
      case "SWF":
      case "STAFF_WELFARE":
        return { className: `${baseClass} bg-danger text-white`, style: {} };
      case "E_ATTESTATION":
      case "EATTESTATION":
        return { className: `${baseClass} bg-primary text-white`, style: {} };
      case "MEDICAL_ATTENDANCE":
      case "MEDICAL":
        return { className: `${baseClass} bg-danger text-white`, style: {} };
      case "LEAVE_ENCASH":
      case "LEAVE_ENCASHMENT":
        return { className: `${baseClass} bg-warning text-dark`, style: {} };
      case "HALL_BOOKING":
      case "COMMUNITY_HALL":
        return { className: `${baseClass} bg-danger text-white`, style: {} };
      case "SV4":
      case "SV4_CARD":
        return { className: `${baseClass} bg-danger text-white`, style: {} };
      case "SELF_TRANSFER":
      case "TRANSFER_POSTING":
      case "TRANSFER":
        return { className: `${baseClass}`, style: { backgroundColor: '#4F46E5', color: 'white' } };
      default:
        if (isSelfTransferWorklistItem(task)) {
          return { className: `${baseClass}`, style: { backgroundColor: '#4F46E5', color: 'white' } };
        }
        if (isSv4WorklistItem(task)) {
          return { className: `${baseClass} bg-danger text-white`, style: {} };
        }
        return { className: `${baseClass} bg-secondary text-white`, style: {} };
    }
  };

  const isNocTask = (task: WorklistItem): boolean => {
    const taskText = task.taskText?.toLowerCase() || "";
    const type = task.type?.toLowerCase() || "";
    return type.includes("noc") || taskText.includes("noc");
  };

  const getNocType = (
    task: WorklistItem
  ): "NOC_OUTJOB" | "NOC_EXINDIA" | "NOC_HIGHERSTUDY" | "NOC_PASSPORT" | "NOC_ONLINECOURSE" | "NOC_ONLINECOURSE-REIMB" => {
    const taskText = task.taskText?.toLowerCase() || "";
    const workItemText = task.workItemText?.toLowerCase() || "";
    const type = task.type || "";

    // Check type first for exact match
    if (type === "NOC_HIGHERSTUDY") {
      return "NOC_HIGHERSTUDY";
    }
    if (type === "NOC_EXINDIA") {
      return "NOC_EXINDIA";
    }
    if (type === "NOC_PASSPORT") {
      return "NOC_PASSPORT";
    }
    if (type === "NOC_ONLINECOURSE-REIMB" || type === "NOC_ONLINECOURSE_REIMB") {
      return "NOC_ONLINECOURSE-REIMB";
    }
    if (type === "NOC_ONLINECOURSE") {
      return "NOC_ONLINECOURSE";
    }
    if (type === "NOC_OUTJOB") {
      return "NOC_OUTJOB";
    }

    // Fallback to text-based detection
    if (taskText.includes("reimb") || workItemText.includes("reimb")) {
      return "NOC_ONLINECOURSE-REIMB";
    }
    if (taskText.includes("exindia") || workItemText.includes("ex-india")) {
      return "NOC_EXINDIA";
    }
    if (
      taskText.includes("higher") ||
      taskText.includes("studies") ||
      taskText.includes("education") ||
      workItemText.includes("higher studies") ||
      workItemText.includes("higher education")
    ) {
      return "NOC_HIGHERSTUDY";
    }
    if (taskText.includes("passport") || workItemText.includes("passport")) {
      return "NOC_PASSPORT";
    }
    if (
      taskText.includes("online") ||
      taskText.includes("course") ||
      workItemText.includes("online course")
    ) {
      return "NOC_ONLINECOURSE";
    }

    // Default to outside job
    return "NOC_OUTJOB";
  };

  const formatCreationTime = (time: string | undefined): string => {
    if (!time) return "Not available";
    try {
      // Handle SAP time format (HHMMSS)
      if (time.length === 6) {
        const hours = time.substring(0, 2);
        const minutes = time.substring(2, 4);
        const seconds = time.substring(4, 6);
        return `${hours}:${minutes}:${seconds}`;
      }
      return new Date(time).toLocaleTimeString("en-IN");
    } catch {
      return time;
    }
  };

  // Fetch data on component mount and page change
  useEffect(() => {
    console.log("TasksPage useEffect triggered, currentPage:", pagination.page);
    fetchData();
  }, [pagination.page]);

  const fetchData = useCallback(() => {
    const params = {
      page: pagination.page.toString(),
      limit: pagination.limit.toString(),
      orderColumn: "created_at",
      orderBy: "DESC",
      filters: filters,
    };
    dispatch(fetchSapWorklist(params));
  }, [dispatch, pagination.page, pagination.limit, filters]);

  const handleSync = async () => {
    try {
      await dispatch(syncWorklistCurrentDay() as any).unwrap();
      showToast("Success", "Sync triggered successfully", "success");
      fetchData();
    } catch (err: any) {
      showToast("Error", err?.message || "Sync failed", "danger");
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    if (key.includes("dateRange.")) {
      const field = key.split(".")[1];
      setFilters((prev) => ({
        ...prev,
        dateRange: { ...prev.dateRange, [field]: value },
      }));
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  const applyFilters = () => {
    dispatch(setCurrentPage(1));
    fetchData();
  };

  const clearFilters = () => {
    setFilters({
      dateRange: { type: "", from: "", to: "" },
      moduleType: "",
      claimType: "",
      referenceNumber: "",
    });
    dispatch(setCurrentPage(1));
    fetchData(); // will be triggered by useEffect? Actually wait, useEffect only listens to pagination.page. 
    // It's safer to just let applyFilters do it since useEffect relies on pagination.page change which might not change if it's already 1.
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
  };


  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    dispatch(setSelectedTask(null));
    dispatch(clearNocData());
  };

  const openDetailsModal = (task: WorklistItem) => {
    // Navigate to details page instead of opening modal
    handleViewDetails(task);
  };

  const handleViewDetails = (task: WorklistItem) => {
    const taskText = task.taskText?.toLowerCase() || "";
    const workItemText = task.workItemText?.toLowerCase() || "";
    const type = task.type?.toLowerCase() || "";

    if (isStaffWelfareWorklistItem(task)) {
      const url = `/todo-worklist/staff-welfare/${encodeURIComponent(
        String(task.requestID || task.id)
      )}`;
      navigate(url);
      return;
    }

    if (isEAttestationWorklistItem(task)) {
      const url = `/todo-worklist/e-attestation-approval/${encodeURIComponent(
        String(task.requestID || task.id)
      )}`;
      navigate(url);
      return;
    }

    if (isMedicalAttendanceWorklistItem(task)) {
      const url = `/todo-worklist/medical-attendance-approval/${encodeURIComponent(
        String(task.requestID || task.id)
      )}`;
      navigate(url);
      return;
    }

    if (isHallBookingWorklistItem(task)) {
      const url = `/todo-worklist/hall-booking-approval/${encodeURIComponent(
        String(task.requestID || task.id)
      )}`;
      navigate(url);
      return;
    }

    if (isLeaveEncashmentWorklistItem(task)) {
      const url = `/todo-worklist/leave-encashment-approval/${encodeURIComponent(
        String(task.requestID || task.id)
      )}`;
      navigate(url);
      return;
    }

    // App: type SV4 → SV4CardApprovalScreen with requestID
    if (isSv4WorklistItem(task)) {
      navigate(getSv4ApprovalPath(task));
      return;
    }

    if (isSelfTransferWorklistItem(task)) {
      const targetId = task.requestID || task.id;
      const url = `/todo-worklist/transfer-posting-approval/${encodeURIComponent(String(targetId))}?workItemId=${task.workItemId || ""}`;
      navigate(url);
      return;
    }

    if (
      type.includes("noc") ||
      taskText.includes("noc") ||
      workItemText.includes("noc") ||
      type.includes("exindia") ||
      type.includes("ex-india") ||
      type.includes("ex_india")
    ) {
      const url = `/todo-worklist/noc-task-details/${task.requestID || task.id}?type=${task.type || "NOC_EXINDIA"}&workItemId=${task.workItemId}`;
      navigate(url);
      return;
    }

    if (taskText.includes("leave") || workItemText.includes("leave") || type.includes("leave")) {
      const url = `/todo-worklist/leave-approval-page/${task.requestID || task.id}?workItemId=${task.workItemId}`;
      navigate(url);
    } else if (taskText.includes("i-card") || taskText.includes("id-card") || taskText.includes("identity card") ||
      workItemText.includes("i-card") || workItemText.includes("id-card") || workItemText.includes("identity card")) {
      const url = `/todo-worklist/id-card-approval/${task.requestID || task.id}`;
      navigate(url);
    } else if (
      taskText.includes("requisition") || workItemText.includes("requisition") ||
      type.includes("requisition") || type.includes("e-requisition")
    ) {
      const url = `/e-requisition-it/details/${task.requestID || task.id}?isApprover=true`;
      navigate(url, { state: { isApprover: true, fromUniversalWorklist: true } });
    } else if (
      taskText.includes("sap authorization") || workItemText.includes("sap authorization") ||
      type.includes("sap_authorization") || type.includes("sap_auth") || type === "sap_authorization_form"
    ) {
      const url = `/sap-authorization/approval/${task.requestID || task.id}`;
      navigate(url);
    } else if (
      taskText.includes("it-declaration") || taskText.includes("it declaration") || workItemText.includes("it-declaration") || workItemText.includes("it declaration") ||
      type.includes("it_declaration") || type.includes("IT_DECLARATION") || type.includes("it-declaration")
    ) {
      const url = `/todo-worklist/it-declaration-approval/${task.requestID || task.id}?workItemId=${task.workItemId}`;
      navigate(url);
    } else if (
      taskText.includes("reimbursement") || workItemText.includes("reimbursement") ||
      type.includes("reimbursement") || type.includes("REIMBURSEMENT")
    ) {
      const workId = task.workItemId || (task as any).workitem_id || (task as any).workItemID || "";
      const reqId = task.requestID || task.id;
      const url = `/todo-worklist/reimbursement-approval/${reqId}?workItemId=${workId}`;
      navigate(url, { state: { workItemId: workId, task } });
    } else {
      const url = `/todo-worklist/noc-task-details/${task.requestID || task.id}?type=${task.type}`;
      navigate(url);
    }
  };

  const handleViewNocDetails = (task: WorklistItem) => {
    if (task.requestID) {
      const nocType = getNocType(task);
      const url = `/todo-worklist/noc-task-details/${task.requestID}?type=${nocType}`;
      navigate(url);
    }
  };



  const handleApproveReject = async (action: "A" | "R", task: WorklistItem) => {
    try {
      const result = await dispatch(
        approveRejectTask({ action, task })
      ).unwrap();

      if (result.success) {
        const actionText = action === "A" ? "approved" : "rejected";
        showToast("Success", `Task ${actionText} successfully`, "success");
        closeDetailsModal();
        // Refresh the list to show updated status
        fetchData();
      }
    } catch (err: any) {
      showToast(
        "Error",
        err || `Failed to ${action === "A" ? "approve" : "reject"} task`,
        "danger"
      );
      console.error("Approve/Reject error:", err);
    }
  };

  // Clear errors on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearNocError());
    };
  }, [dispatch]);

  return (
    <UniversalWorklistLayout>
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                Tasks ({worklistData.totalItems})
              </h5>
              <div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="btn btn-outline-secondary btn-sm me-2"
                >
                  <i className="ti ti-filter me-1"></i>
                  Filters
                </button>
                <button
                  onClick={handleSync}
                  className="btn btn-outline-info btn-sm me-2"
                  disabled={syncLoading}
                >
                  <i className="ti ti-reload me-1"></i>
                  {syncLoading ? "Syncing..." : "Sync"}
                </button>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading SAP worklist...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger d-flex align-items-center">
                  <i className="ti ti-alert-circle me-2"></i>
                  <span>{error}</span>
                  <button
                    type="button"
                    className="btn-close ms-auto"
                    onClick={() => dispatch(clearError())}
                  ></button>
                </div>
              ) : worklistData.data && worklistData.data.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th>Task</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Creator</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {worklistData.data.map((task: WorklistItem) => (
                          <tr key={task.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <i
                                  className={`${getTaskIcon(
                                    task
                                  )} me-2 text-primary`}
                                ></i>
                                <div>
                                  <div className="fw-medium">
                                    <button
                                      className="btn btn-link p-0 text-start fw-medium text-decoration-none"
                                      onClick={() => handleViewDetails(task)}
                                      style={{
                                        color: "#0d6efd",
                                        border: "none",
                                        background: "none",
                                      }}
                                    >
                                      {task.workItemText}
                                    </button>
                                  </div>
                                  <small className="text-muted">
                                    {task.taskText}
                                  </small>
                                  {task.requestID && (
                                    <small className="d-block text-info">
                                      ID: {task.requestID}
                                    </small>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              {(() => {
                                const badgeConfig = getTaskTypeBadge(task);
                                return (
                                  <span
                                    className={badgeConfig.className}
                                    style={badgeConfig.style}
                                  >
                                    {getWorklistTypeLabel(task)}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              <span
                                className={`badge ${task.status === "READY"
                                  ? "bg-success"
                                  : task.status === "COMPLETED"
                                    ? "bg-primary"
                                    : task.status === "APPROVED"
                                      ? "bg-success"
                                      : task.status === "REJECTED"
                                        ? "bg-danger"
                                        : task.status === "IN_PROGRESS"
                                          ? "bg-warning"
                                          : "bg-secondary"
                                  }`}
                              >
                                {task.status}
                              </span>
                            </td>
                            <td>
                              <span className="font-monospace small">
                                {task.creator.trim()}
                              </span>
                            </td>
                            <td>
                              <small>
                                {task.creationDate
                                  ? new Date(
                                    task.creationDate
                                  ).toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  })
                                  : "N/A"}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {worklistData.totalItems > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                      <div>
                        <small className="text-muted">
                          Showing{" "}
                          {worklistData.totalItems > 0
                            ? `${(pagination.page - 1) * pagination.limit + 1}-${Math.min(
                              pagination.page * pagination.limit,
                              worklistData.totalItems
                            )}`
                            : "0"}{" "}
                          of {worklistData.totalItems} tasks
                        </small>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        {/* Previous Button */}
                        <button
                          className="btn btn-sm"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1 || loading}
                          style={{
                            backgroundColor: pagination.page <= 1 ? '#f5f5f5' : 'white',
                            borderColor: '#dee2e6',
                            color: pagination.page <= 1 ? '#6c757d' : '#212529',
                            minWidth: '80px',
                            cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Previous
                        </button>

                        {/* Page Numbers */}
                        {(() => {
                          const totalPages = pagination.totalPages || Math.ceil(worklistData.totalItems / pagination.limit);
                          const pages: (number | string)[] = [];

                          if (totalPages <= 7) {
                            // Show all pages if 7 or fewer
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            // Show pages with ellipsis
                            if (pagination.page <= 4) {
                              // Show first 5 pages, then ellipsis, then last page
                              for (let i = 1; i <= 5; i++) {
                                pages.push(i);
                              }
                              pages.push('...');
                              pages.push(totalPages);
                            } else if (pagination.page >= totalPages - 3) {
                              // Show first page, ellipsis, then last 5 pages
                              pages.push(1);
                              pages.push('...');
                              for (let i = totalPages - 4; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              // Show first page, ellipsis, current-1, current, current+1, ellipsis, last page
                              pages.push(1);
                              pages.push('...');
                              pages.push(pagination.page - 1);
                              pages.push(pagination.page);
                              pages.push(pagination.page + 1);
                              pages.push('...');
                              pages.push(totalPages);
                            }
                          }

                          return pages.map((page, index) => {
                            if (page === '...') {
                              return (
                                <span key={`ellipsis-${index}`} className="px-2 text-muted">
                                  ...
                                </span>
                              );
                            }
                            const pageNum = page as number;
                            const isActive = pageNum === pagination.page;
                            return (
                              <button
                                key={pageNum}
                                className="btn btn-sm"
                                onClick={() => handlePageChange(pageNum)}
                                disabled={loading}
                                style={{
                                  backgroundColor: isActive ? '#fd7e14' : 'white',
                                  color: isActive ? 'white' : '#212529',
                                  borderColor: '#dee2e6',
                                  minWidth: '40px',
                                  cursor: loading ? 'not-allowed' : 'pointer',
                                  fontWeight: isActive ? 'bold' : 'normal'
                                }}
                              >
                                {pageNum}
                              </button>
                            );
                          });
                        })()}

                        {/* Next Button */}
                        <button
                          className="btn btn-sm"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={
                            pagination.page >= pagination.totalPages || loading
                          }
                          style={{
                            backgroundColor: pagination.page >= pagination.totalPages ? '#f5f5f5' : 'white',
                            borderColor: '#dee2e6',
                            color: pagination.page >= pagination.totalPages ? '#6c757d' : '#212529',
                            minWidth: '80px',
                            cursor: pagination.page >= pagination.totalPages ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="ti ti-inbox fs-1 text-muted"></i>
                  <p className="mt-2 text-muted">
                    {error
                      ? "Failed to load SAP data. Check the error above."
                      : "No tasks found in SAP worklist"}
                  </p>
                  <button
                    onClick={handleRefresh}
                    className="btn btn-primary mt-2"
                    disabled={loading}
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Task Details Modal */}
      {showDetailsModal && selectedTask && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  <i className={getTaskIcon(selectedTask)}></i>
                  <span className="ms-2">
                    Task Details - {selectedTask.workItemText}
                  </span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDetailsModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    {/* Quick Status Overview */}
                    <div className="row mb-4">
                      <div className="col-md-3">
                        <div className="text-center">
                          <div className="h4 mb-1">
                            {(() => {
                              const badgeConfig = getTaskTypeBadge(selectedTask);
                              return (
                                <span
                                  className={badgeConfig.className}
                                  style={badgeConfig.style}
                                >
                                  {getTaskType(selectedTask)}
                                </span>
                              );
                            })()}
                          </div>
                          <small className="text-muted">Task Type</small>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-center">
                          <div className="h5 mb-1">
                            <span
                              className={`badge ${selectedTask.status === "READY"
                                ? "bg-success"
                                : selectedTask.status === "COMPLETED"
                                  ? "bg-primary"
                                  : selectedTask.status === "APPROVED"
                                    ? "bg-success"
                                    : selectedTask.status === "REJECTED"
                                      ? "bg-danger"
                                      : selectedTask.status === "IN_PROGRESS"
                                        ? "bg-warning"
                                        : "bg-secondary"
                                }`}
                            >
                              {selectedTask.status}
                            </span>
                          </div>
                          <small className="text-muted">Status</small>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-center">
                          <div className="h6 mb-1 text-primary">
                            {selectedTask.workflowID}
                          </div>
                          <small className="text-muted">Workflow ID</small>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-center">
                          <div className="h6 mb-1 text-info">
                            {selectedTask.requestID || "N/A"}
                          </div>
                          <small className="text-muted">Request ID</small>
                        </div>
                      </div>
                    </div>

                    {/* NOC Specific Actions */}
                    {isNocTask(selectedTask) && selectedTask.requestID && (
                      <div className="row mb-4">
                        <div className="col-12">
                          <div className="card border-info">
                            <div className="card-header bg-info-subtle">
                              <h6 className="mb-0 text-info">
                                <i className="ti ti-file-certificate me-2"></i>
                                NOC Request Details
                              </h6>
                            </div>
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <p className="mb-1">
                                    <strong>NOC Type:</strong>{" "}
                                    {getNocType(selectedTask).replace("_", " ")}
                                  </p>
                                  <p className="mb-0 text-muted">
                                    View comprehensive NOC information in a
                                    dedicated page
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-info"
                                  onClick={() =>
                                    handleViewNocDetails(selectedTask)
                                  }
                                >
                                  <i className="ti ti-external-link me-2"></i>
                                  View NOC Details
                                </button>
                              </div>

                              {nocError && (
                                <div className="alert alert-danger mt-3 mb-0">
                                  <i className="ti ti-alert-circle me-2"></i>
                                  {nocError}
                                  <button
                                    type="button"
                                    className="btn-close ms-auto"
                                    onClick={() => dispatch(clearNocError())}
                                  ></button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-md-8">
                        {/* Task Information */}
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">
                              <i className="ti ti-info-circle me-2"></i>
                              Task Information
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="mb-3">
                              <strong>Subject:</strong>
                              <p className="mt-1 mb-2">
                                {selectedTask.workItemText}
                              </p>
                            </div>
                            <div className="mb-3">
                              <strong>Task Description:</strong>
                              <p className="mt-1 mb-2 text-muted">
                                {selectedTask.taskText}
                              </p>
                            </div>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="mb-2">
                                  <strong>Work Item ID:</strong>
                                  <div className="font-monospace small text-primary">
                                    {selectedTask.workItemId}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-2">
                                  <strong>SAP Type:</strong>
                                  <div className="text-muted">
                                    {getWorklistTypeLabel(selectedTask)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Process Information */}
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">
                              <i className="ti ti-users me-2"></i>
                              Process Information
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-6">
                                <div className="mb-2">
                                  <strong>Approver ID:</strong>
                                  <span className="ms-2 badge bg-light text-dark">
                                    {selectedTask.approverId}
                                  </span>
                                </div>
                                <div className="mb-2">
                                  <strong>Employee ID:</strong>
                                  <span className="ms-2">
                                    {selectedTask.empId || "Not specified"}
                                  </span>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-2">
                                  <strong>Creator:</strong>
                                  <div className="font-monospace small text-muted">
                                    {selectedTask.creator.trim()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4">
                        {/* Date & Time Information */}
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">
                              <i className="ti ti-calendar me-2"></i>
                              Timeline
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="mb-3">
                              <strong>Created On:</strong>
                              <div className="text-muted small">
                                {selectedTask.creationDate
                                  ? new Date(
                                    selectedTask.creationDate
                                  ).toLocaleDateString("en-IN", {
                                    weekday: "short",
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })
                                  : "Not available"}
                              </div>
                              <div className="text-muted small">
                                at{" "}
                                {formatCreationTime(selectedTask.creationTime)}
                              </div>
                            </div>
                            <div className="mb-3">
                              <strong>Last Modified:</strong>
                              <div className="text-muted small">
                                {new Date(
                                  selectedTask.modified_at
                                ).toLocaleDateString("en-IN")}
                              </div>
                              <div className="text-muted small">
                                {new Date(
                                  selectedTask.modified_at
                                ).toLocaleTimeString("en-IN")}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* System Information */}
                        <div className="card border-0 shadow-sm">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">
                              <i className="ti ti-database me-2"></i>
                              System Info
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="mb-2">
                              <strong>Internal ID:</strong>
                              <div className="text-muted small">
                                {selectedTask.id}
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>Record Status:</strong>
                              <div className="text-muted small">
                                {selectedTask.deleted_at ? "Deleted" : "Active"}
                              </div>
                            </div>
                            {selectedTask.sapCreatedAt && (
                              <div className="mb-2">
                                <strong>SAP Created:</strong>
                                <div className="text-muted small">
                                  {selectedTask.sapCreatedAt}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  onClick={closeDetailsModal}
                >
                  <i className="ti ti-x me-2"></i>
                  Close
                </button>
                {selectedTask.status === "READY" && (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-success me-2"
                      onClick={() => handleApproveReject("A", selectedTask)}
                      disabled={
                        actionLoading.taskId === selectedTask.id &&
                        actionLoading.action === "A"
                      }
                    >
                      {actionLoading.taskId === selectedTask.id &&
                        actionLoading.action === "A" ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Approving...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-check me-2"></i>
                          Approve
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => handleApproveReject("R", selectedTask)}
                      disabled={
                        actionLoading.taskId === selectedTask.id &&
                        actionLoading.action === "R"
                      }
                    >
                      {actionLoading.taskId === selectedTask.id &&
                        actionLoading.action === "R" ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Rejecting...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-x me-2"></i>
                          Reject
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {showFilters && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Filter Options</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowFilters(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Reference Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={filters.referenceNumber}
                      onChange={(e) => handleFilterChange("referenceNumber", e.target.value)}
                      placeholder="Enter ref number"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Module Type</label>
                    <Select
                      options={moduleTypeOptions}
                      value={moduleTypeOptions.find(o => o.value === filters.moduleType) || null}
                      onChange={(selected: any) => {
                        const val = selected ? selected.value : "";
                        handleFilterChange("moduleType", val);
                        if (val !== "REIMBURSEMENT") {
                          handleFilterChange("claimType", "");
                        }
                      }}
                      placeholder="Select Module Type..."
                      isClearable={false}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                  {filters.moduleType === "REIMBURSEMENT" && (
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Claim Type</label>
                      <Select
                        options={claimTypeOptionsList}
                        value={claimTypeOptionsList.find(o => o.value === filters.claimType) || null}
                        onChange={(selected: any) => handleFilterChange("claimType", selected ? selected.value : "")}
                        placeholder="Select Claim Type..."
                        isClearable={false}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                  )}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Date Range</label>
                    <Select
                      options={dateRangeOptions}
                      value={dateRangeOptions.find(o => o.value === filters.dateRange.type) || null}
                      onChange={(selected: any) => {
                        const val = selected ? selected.value : "";
                        handleFilterChange("dateRange.type", val);
                        if (val !== "CUSTOM") {
                          handleFilterChange("dateRange.from", "");
                          handleFilterChange("dateRange.to", "");
                        }
                      }}
                      placeholder="Select Date Range..."
                      isClearable={false}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                  {filters.dateRange.type === "CUSTOM" && (
                    <>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Date From</label>
                        <input
                          type="date"
                          className="form-control"
                          value={filters.dateRange.from}
                          onChange={(e) => handleFilterChange("dateRange.from", e.target.value)}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Date To</label>
                        <input
                          type="date"
                          className="form-control"
                          value={filters.dateRange.to}
                          onChange={(e) => handleFilterChange("dateRange.to", e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer justify-content-between">
                <button className="btn btn-outline-danger" style={{ minWidth: "120px" }} onClick={() => { clearFilters(); setShowFilters(false); }}>Reset</button>
                <button className="btn btn-danger" style={{ minWidth: "120px" }} onClick={() => { applyFilters(); setShowFilters(false); }}>Apply Filters</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <Toasts
        show={toast.show}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />
    </UniversalWorklistLayout>
  );
};

export default TasksPage;
